// import { type Reference } from '@apollo/client';
import {
  Button,
  ButtonColor,
  IconHeart,
  IconHeartFilled,
} from '@leland-dev/leland-ui-library';
import { type FC } from 'react';

import { type Scalars } from '../../__generated-gql-types__/globalTypes';
import { useAuth } from '../../context/AuthContext';
import { LexSlug, useLex } from '../../context/LexContext';
import phog from '../../utils/analytics/posthog/posthog';

import { useSaveCoachButtonUpdateSavedCoachMutation } from './__generated-gql-types__/SaveCoachButton.generated';

export interface SaveCoachButtonProps {
  coachUrn: Scalars['CoachUrn']['output'];
  isSaved: boolean;
}

export const SaveCoachButton: FC<SaveCoachButtonProps> = ({
  coachUrn,
  isSaved,
}) => {
  const { currentUser } = useAuth();
  const { isLexEnabled } = useLex();

  const [updateSavedCoach] = useSaveCoachButtonUpdateSavedCoachMutation();

  if (
    !currentUser?.applicant ||
    !isLexEnabled(LexSlug.SAVING_COACHES_AB_ENABLED)
  ) {
    return null;
  }

  const handleToggleSave = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (!currentUser?.applicant) {
      //TODO: Launch signup modal
      return;
    }

    phog.capture('save_coach_button--click', {
      coachUrn,
      action: isSaved ? 'unsave' : 'save',
    });

    await updateSavedCoach({
      variables: {
        coachUrn,
        saved: !isSaved,
      },
      optimisticResponse: {
        updateSavedCoach: {
          __typename: 'Applicant',
          id: currentUser?.applicant.id,
          savedCoaches: isSaved
            ? currentUser?.applicant.savedCoaches.filter(
                (coach) => coach.id !== coachUrn,
              )
            : [
                ...(currentUser?.applicant.savedCoaches || []),
                { id: coachUrn },
              ],
        },
      },
      update: (cache) => {
        if (!currentUser?.applicant) {
          return;
        }
        // Update the Coach cache
        cache.modify({
          id: cache.identify({
            __typename: 'Coach',
            id: coachUrn,
          }),
          fields: {
            isSaved: () => {
              return !isSaved;
            },
          },
        });
      },
    });
  };

  if (isSaved) {
    return (
      <Button
        id="coach-card--unsave-coach"
        LeftIcon={IconHeartFilled}
        label=""
        hideLabel
        buttonColor={ButtonColor.LIGHT_RED}
        onClick={handleToggleSave}
      />
    );
  } else {
    return (
      <Button
        id="coach-card--save-coach"
        LeftIcon={IconHeart}
        label=""
        hideLabel
        buttonColor={ButtonColor.WHITE}
        onClick={handleToggleSave}
      />
    );
  }
};
